// AgentRequestsPanel — Phase 3.5 consolidated sub-agent view.
//
// Mirror of PlayerRequestsPanel for the Agents side. Replaces
// /finance/agents for the agent SPA. Structure (top to bottom):
//   1. QRCodeModule                — same QR (parent's one UPI)
//   2. Recharge / Withdrawal       — magenta hero toggle
//   3. ManualSubAgentRechargeForm  — Recharge view only
//   4. Pending / History           — magenta sub-tabs
//   5. Request cards               — sub-agent recharge or withdrawal
//
// Endpoints:
//   GET  /admin/me-agent/payment-requests/agent-recharge
//   POST .../agent-recharge/:id/approve | reject
//   GET  /admin/me-agent/payment-requests/agent-withdrawal
//   POST .../agent-withdrawal/:id/mark-paid | reject
//
// Wallet sync — same shape as the player panel:
//   - refreshAgentWallet() on mount
//   - refreshAgentWallet() after every approve / reject / mark-paid
//   - ManualSubAgentRechargeForm pushes parentBalance to the store

import { ReactNode, useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { refreshAgentWallet } from '@baaligames/shared-ui/admin/lib/agentWallet';
import AdminAvatar from '@baaligames/shared-ui/admin/components/AdminAvatar';
import Icon from '@baaligames/shared-ui/components/Icon';
import PhoneContact from '@baaligames/shared-ui/components/PhoneContact';
import WhatsAppIcon from '@baaligames/shared-ui/components/WhatsAppIcon';
import MagentaTabBar from '@baaligames/shared-ui/admin/components/MagentaTabBar';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import ApprovalEditModal from '../../components/ApprovalEditModal';
import DangerConfirmModal from '@baaligames/shared-ui/components/DangerConfirmModal';
import ProofImageViewer, { type ProofState } from '@baaligames/shared-ui/components/ProofImageViewer';
import ProofImagePicker from '@baaligames/shared-ui/components/ProofImagePicker';
import ProofImagePickerChip from '@baaligames/shared-ui/components/ProofImagePickerChip';
import QRCodeModule from '../../components/QRCodeModule';
// 2026-05-16: replaced ManualSubAgentRechargeForm with the new
// DirectAdjustCard (scope='subagents'). Same backend endpoint
// (POST /admin/me-agent/sub-agents/:id/wallet/topup) but the new
// card matches the admin SPA's compact "browse → pick → modal"
// pattern, surfacing a SUB-AGENT badge, level pill, UAID, parent
// pool ("your pool" funding-source chip) so the agent
// double-confirms the right child before submitting.
import DirectAdjustCard from '../../components/DirectAdjustCard';

// ─── Types ─────────────────────────────────────────────────────────

// Phase 18 — proof columns. SELECT r.* / w.* on the model side
// propagates these automatically.
type ProofColumns = {
  proof_image_url: string | null;
  proof_uploaded_at: string | null;
  payout_proof_url: string | null;
  payout_proof_uploaded_at: string | null;
  payout_proof_view_count: number;
};

type AgentRechargeRow = ProofColumns & {
  id: number;
  agent_id: number;
  agent_username: string;
  agent_avatar_url: string | null;
  agent_email: string;
  // Phase C4 — Identity Stack for direct downline. Parent agent sees
  // their direct sub-agent's contact phone in the queue row.
  agent_phone: string | null;
  agent_first_name?: string | null;
  agent_last_name?: string | null;
  agent_display_name?: string;
  amount: number;
  // Phase 10 (2026-05-12): target tells the parent whether this is a
  // wallet recharge (default) or a deposit recharge; payment_method
  // tells them whether the agent paid via UPI or cash. The row card
  // surfaces both with distinct icons + tones so the parent can
  // triage the inbox at a glance without opening each row.
  target?: 'wallet' | 'deposit';
  payment_method?: 'upi' | 'cash';
  approved_amount: number | null;
  transaction_id: string;
  agent_note: string | null;
  state: 'pending'|'approved'|'rejected'|'expired';
  decided_at: string | null;
  decision_note: string | null;
  ledger_id: number | null;
  created_at: string;
};

type AgentWithdrawalRow = ProofColumns & {
  id: number;
  agent_id: number;
  agent_username: string;
  agent_avatar_url: string | null;
  agent_email: string;
  // Phase C4 — Identity Stack for direct downline.
  agent_phone: string | null;
  agent_first_name?: string | null;
  agent_last_name?: string | null;
  agent_display_name?: string;
  amount: number;
  // Phase 10: same target + payment_method semantics as above.
  // payment_method on a withdrawal is how the AGENT wants to be paid
  // (the parent honours that channel when they mark-paid).
  target?: 'wallet' | 'deposit';
  payment_method?: 'upi' | 'cash';
  approved_amount: number | null;
  upi_id: string;
  whatsapp_number: string | null;
  agent_note: string | null;
  state: 'pending'|'approved'|'rejected'|'paid'|'expired';
  decided_at: string | null;
  decision_note: string | null;
  paid_transaction_id: string | null;
  paid_at: string | null;
  hold_ledger_id: number | null;
  created_at: string;
};

type Direction = 'recharge' | 'withdrawal';
type Sub = 'pending' | 'history';

const LIMIT = 50;
const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;

// ─── Phase 18 — proof helpers (parent agent → sub-agent context) ──
const VIEW_EXPIRY_MS   = 72 * 60 * 60 * 1000;
const UPLOAD_WINDOW_MS = 24 * 60 * 60 * 1000;
function proofStateFor(uploadedAt: string | null, url: string | null): ProofState {
  if (!uploadedAt) return 'none';
  const age = Date.now() - new Date(uploadedAt).getTime();
  if (age > VIEW_EXPIRY_MS) return 'expired';
  return url ? 'available' : 'expired';
}
function canUploadPayoutProof(paidAt: string | null, state: string, hasProof: boolean): boolean {
  if (hasProof) return false;
  if (state !== 'paid') return false;
  if (!paidAt) return false;
  return (Date.now() - new Date(paidAt).getTime()) < UPLOAD_WINDOW_MS;
}

/**
 * PayoutProofCell (parent-agent → sub-agent variant). Hits the
 * me-agent payment-requests endpoint so the server's
 * agentChainContains gate verifies the caller is in the upline
 * cascade for this sub-agent's withdrawal.
 */
function PayoutProofCell({
  row, uploadEndpoint, viewEndpoint, onUploaded,
}: {
  row: { state: string; paid_at: string | null;
         payout_proof_url: string | null; payout_proof_uploaded_at: string | null };
  uploadEndpoint: string;
  viewEndpoint: string;
  onUploaded?: () => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (row.state !== 'paid') return null;
  const proofState = proofStateFor(row.payout_proof_uploaded_at, row.payout_proof_url);
  if (proofState === 'available' || proofState === 'expired') {
    return <ProofImageViewer state={proofState} endpoint={viewEndpoint} apiGet={(p) => adminApi.get(p)} />;
  }
  const can = canUploadPayoutProof(row.paid_at, row.state, false);
  if (!can) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold
                       bg-arena-100/70 text-arena-500 ring-1 ring-inset ring-arena-200/50
                       dark:bg-arena-700/40 dark:text-neon-200/55 dark:ring-arena-600/40">
        Upload window closed
      </span>
    );
  }
  async function send(blob: Blob) {
    setUploading(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append('proof', blob, 'payout.jpg');
      await adminApi.postForm(uploadEndpoint, fd);
      onUploaded?.();
    } catch (e: any) {
      setErr(e?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }
  return (
    // Compact chip — keeps the card's narrow right column compact so
    // the content column doesn't get squeezed on small screens.
    <span className="inline-flex items-center gap-2 flex-wrap justify-end">
      <ProofImagePickerChip
        onFile={(b) => send(b)}
        onError={(m) => setErr(m)}
        disabled={uploading}
      />
      {err && <span className="text-[11px] text-rose-700 dark:text-rose-300">{err}</span>}
    </span>
  );
}
const fmtDate = (s: string) => new Date(s).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

const BASE = '/admin/me-agent/payment-requests';

// ─── Panel ─────────────────────────────────────────────────────────

export default function AgentRequestsPanel() {
  const t = useT();
  // When a sub-agent detail page's "Recharge" CTA deep-links here with
  // ?preselect=<id>, force the Recharge direction AND hand the id to
  // DirectAdjustCard, which resolves that sub-agent and pops the
  // recharge modal open immediately (2026-08-06 UX fix — no
  // re-searching for someone you just came from).
  const [searchParams] = useSearchParams();
  const preselectId = Number(searchParams.get('preselect')) || undefined;
  const hasPreselect = !!preselectId;
  const [direction, setDirection] = useState<Direction>('recharge');
  const [sub, setSub] = useState<Sub>('pending');

  useEffect(() => {
    if (hasPreselect) setDirection('recharge');
  }, [hasPreselect]);

  const [counts, setCounts] = useState<{ recharge: number; withdrawal: number }>({ recharge: 0, withdrawal: 0 });

  const refreshCounts = useCallback(async () => {
    try {
      const [r, w] = await Promise.all([
        adminApi.get<{ total: number }>(`${BASE}/agent-recharge?state=pending&limit=1&offset=0`),
        adminApi.get<{ total: number }>(`${BASE}/agent-withdrawal?state=pending&limit=1&offset=0`),
      ]);
      setCounts({ recharge: r.total, withdrawal: w.total });
    } catch { /* surfaced in queue */ }
  }, []);

  useEffect(() => {
    refreshAgentWallet();
    refreshCounts();
  }, [refreshCounts]);

  return (
    <div className="space-y-4">
      {/* audience="agents" flips the QR card's "what … see" label to
          "agents" with the section-emerald highlight. accent="emerald"
          recolors the edit button + modal so the whole card stays on
          the Agents section colour. */}
      <QRCodeModule audience="agents" accent="emerald" />

      <MagentaTabBar
        size="lg"
        accent="emerald"
        activeKey={direction}
        onChange={(k) => { setDirection(k as Direction); setSub('pending'); }}
        items={[
          // Phase R7 follow-up (2026-05-13): per-direction pending
          // counts on the hero toggle so the workload split is
          // always visible. Magenta badges (handled in
          // MagentaTabBar) pop against the emerald accent fill.
          { key: 'recharge',   label: t('agent.agentRequests.direction.recharge'),   icon: 'arrow_upward',   count: counts.recharge },
          { key: 'withdrawal', label: t('agent.agentRequests.direction.withdrawal'), icon: 'arrow_downward', count: counts.withdrawal },
        ]}
      />

      {/* Direct sub-agent recharge — recharge view only. Agent →
          sub-agent withdrawals are not a thing (the legacy
          walletWithdraw method was removed per business rule).
          Sub-agents must submit a withdrawal request which this
          parent then marks paid via the queue. */}
      {direction === 'recharge' && (
        <DirectAdjustCard scope="subagents" onAfterAdjust={refreshCounts} preselectId={preselectId} />
      )}

      <MagentaTabBar
        size="sm"
        accent="emerald"
        activeKey={sub}
        onChange={(k) => setSub(k as Sub)}
        items={[
          { key: 'pending', label: t('agent.agentRequests.tab.pending'), icon: 'inbox',   count: counts[direction] },
          { key: 'history', label: t('agent.agentRequests.tab.history'), icon: 'history' },
        ]}
      />

      <Queue direction={direction} sub={sub} onChanged={refreshCounts} />
    </div>
  );
}

// ─── Queue body ────────────────────────────────────────────────────

function Queue({
  direction, sub, onChanged,
}: {
  direction: Direction; sub: Sub; onChanged: () => void;
}) {
  const t = useT();
  const [rRows, setRRows] = useState<AgentRechargeRow[]>([]);
  const [wRows, setWRows] = useState<AgentWithdrawalRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const endpoint = direction === 'recharge'
    ? `${BASE}/agent-recharge`
    : `${BASE}/agent-withdrawal`;

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      if (sub === 'pending') {
        const r = await adminApi.get<{ rows: any[]; total: number }>(`${endpoint}?state=pending&limit=${LIMIT}&offset=${offset}`);
        if (direction === 'recharge') setRRows(r.rows as AgentRechargeRow[]);
        else                          setWRows(r.rows as AgentWithdrawalRow[]);
        setTotal(r.total);
      } else {
        const states = direction === 'recharge' ? ['approved','rejected'] : ['paid','rejected'];
        const pages = await Promise.all(
          states.map((s) => adminApi.get<{ rows: any[]; total: number }>(`${endpoint}?state=${s}&limit=${LIMIT}&offset=${offset}`))
        );
        const merged = pages.flatMap((p) => p.rows).sort((x: any, y: any) => y.created_at.localeCompare(x.created_at));
        if (direction === 'recharge') setRRows(merged as AgentRechargeRow[]);
        else                          setWRows(merged as AgentWithdrawalRow[]);
        setTotal(pages.reduce((a, p) => a + p.total, 0));
      }
    } catch (e) {
      setErr((e as ApiError).message);
    } finally { setLoading(false); }
  }, [direction, sub, offset, endpoint]);

  useEffect(() => { setOffset(0); }, [direction, sub]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  // ── Approval edit modal state ──
  type ModalCtx =
    | { kind: 'agent-recharge';   row: AgentRechargeRow }
    | { kind: 'agent-withdrawal'; row: AgentWithdrawalRow }
    | null;
  const [modal, setModal] = useState<ModalCtx>(null);
  const [rejectCtx, setRejectCtx] = useState<{ kind: 'recharge' | 'withdrawal'; id: number } | null>(null);

  function openApproveRecharge(id: number) {
    const row = rRows.find((r) => r.id === id);
    if (row) setModal({ kind: 'agent-recharge', row });
  }
  function openMarkPaid(row: AgentWithdrawalRow) {
    setModal({ kind: 'agent-withdrawal', row });
  }

  // One-tap: approve a sub-agent recharge for the FULL requested amount
  // without opening the edit modal (2026-08-06 UX fix). "Edit" still
  // opens the modal for partial approvals. Same money path as the
  // modal's full-amount approve.
  async function approveAgentRechargeFull(row: AgentRechargeRow) {
    const id = row.id;
    setBusyId(id); setErr(null);
    try {
      await adminApi.post(`${BASE}/agent-recharge/${id}/approve`, { approved_amount: row.amount });
      setToast(t('agent.agentRequests.toast.approved', { amount: inr(row.amount) }));
      refreshAgentWallet();
      await load(); onChanged();
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusyId(null); }
  }

  // Reject opens a themed reason sheet (2026-08-06 UX fix — replaced
  // window.prompt). Reason optional; withdrawal rejects refund the
  // sub-agent's held balance. confirmReject does the POST on Confirm.
  function rejectRecharge(id: number)   { setRejectCtx({ kind: 'recharge',   id }); }
  function rejectWithdrawal(id: number) { setRejectCtx({ kind: 'withdrawal', id }); }

  async function confirmReject(reason?: string) {
    if (!rejectCtx) return;
    const { kind, id } = rejectCtx;
    const note = (reason || '').trim();
    setBusyId(id); setErr(null);
    try {
      const path = kind === 'recharge'
        ? `${BASE}/agent-recharge/${id}/reject`
        : `${BASE}/agent-withdrawal/${id}/reject`;
      await adminApi.post(path, note ? { note } : {});
      setToast(kind === 'recharge'
        ? t('agent.agentRequests.toast.rejected')
        : t('agent.agentRequests.toast.rejectedRefunded'));
      refreshAgentWallet();
      setRejectCtx(null);
      await load(); onChanged();
    } catch (e) { setErr((e as ApiError).message); throw e; }
    finally { setBusyId(null); }
  }

  async function onApprovalConfirm(args: { approvedAmount: number; note?: string; paidTransactionId?: string; payoutProofBlob?: Blob | null }) {
    if (!modal) return;
    const id = modal.row.id;
    setBusyId(id); setErr(null);
    try {
      const body: Record<string, any> = { approved_amount: args.approvedAmount };
      if (args.note) body.note = args.note;
      let toastMsg = '';
      let payoutProofUrlPath: string | null = null;
      if (modal.kind === 'agent-recharge') {
        await adminApi.post(`${BASE}/agent-recharge/${id}/approve`, body);
        toastMsg = t('agent.agentRequests.toast.approved', { amount: inr(args.approvedAmount) });
      } else {
        body.paid_transaction_id = args.paidTransactionId!;
        await adminApi.post(`${BASE}/agent-withdrawal/${id}/mark-paid`, body);
        toastMsg = t('agent.agentRequests.toast.markedPaid', { amount: inr(args.approvedAmount) });
        payoutProofUrlPath = `${BASE}/agent-withdrawal/${id}/payout-proof`;
      }
      // Phase 18 — chain the payout-proof upload if attached.
      if (args.payoutProofBlob && payoutProofUrlPath) {
        try {
          const fd = new FormData();
          fd.append('proof', args.payoutProofBlob, 'payout.jpg');
          await adminApi.postForm(payoutProofUrlPath, fd);
          toastMsg += ' · screenshot uploaded';
        } catch (_uploadErr) {
          setErr('Marked paid, but screenshot upload failed. Retry from the queue card within 24 h.');
        }
      }
      // Every approve / mark-paid moves money between the parent
      // agent's pool and the sub-agent's pool. Refresh the global
      // chip so the agent never overspends from a stale view.
      refreshAgentWallet();
      setToast(toastMsg);
      setModal(null);
      await load(); onChanged();
    } catch (e) {
      setErr((e as ApiError).message);
      throw e;
    } finally {
      setBusyId(null);
    }
  }

  const rows: Array<AgentRechargeRow | AgentWithdrawalRow> = direction === 'recharge' ? rRows : wRows;

  const modalRequested = modal?.row.amount ?? 0;
  // 2026-05-18 — Modal counterparty label uses display_name; never
  // falls through to @username (peer privacy).
  const modalLabel = modal == null ? '' :
    (((modal.row as { agent_display_name?: string }).agent_display_name) || '').trim() || '—';

  return (
    <div className="space-y-3">
      <ApprovalEditModal
        open={modal !== null}
        kind={modal?.kind === 'agent-withdrawal' ? 'withdrawal' : 'recharge'}
        requestedAmount={modalRequested}
        counterpartyLabel={modalLabel}
        onCancel={() => setModal(null)}
        onConfirm={onApprovalConfirm}
      />

      {/* Themed reject-reason sheet (replaces window.prompt). Reason
          optional; withdrawal rejects refund the sub-agent's held balance. */}
      <DangerConfirmModal
        open={rejectCtx !== null}
        onClose={() => setRejectCtx(null)}
        onConfirm={confirmReject}
        title={rejectCtx?.kind === 'withdrawal'
          ? t('agent.agentRequests.reject.title.withdrawal')
          : t('agent.agentRequests.reject.title.recharge')}
        description={rejectCtx?.kind === 'withdrawal'
          ? t('agent.agentRequests.reject.body.withdrawal')
          : t('agent.agentRequests.reject.body.recharge')}
        confirmLabel={t('agent.agentRequests.action.reject')}
        reasonLabel={t('agent.agentRequests.reject.reasonLabel')}
        reasonPlaceholder={t('agent.agentRequests.reject.reasonPlaceholder')}
      />

      {err && <Banner tone="error">{err}</Banner>}
      {toast && <Banner tone="success">{toast}</Banner>}

      {loading && rows.length === 0 && (
        <div className="rounded-2xl bg-white dark:bg-arena-700/40 border border-arena-200/60 dark:border-arena-600/40 p-8 text-center text-[13px] text-arena-600 dark:text-neon-200/70">
          {t('agent.agentRequests.loading')}
        </div>
      )}
      {!loading && rows.length === 0 && (
        <div className="rounded-2xl bg-white dark:bg-arena-700/40 border border-arena-200/60 dark:border-arena-600/40 p-8 text-center">
          <Icon name="inbox" size={28} className="text-arena-500 dark:text-neon-200/60 mx-auto mb-2" />
          <div className="text-[13px] text-arena-600 dark:text-neon-200/80">
            {sub === 'pending'
              ? (direction === 'recharge'
                  ? t('agent.agentRequests.empty.pending.recharge')
                  : t('agent.agentRequests.empty.pending.withdrawal'))
              : t('agent.agentRequests.empty.history')}
          </div>
        </div>
      )}

      {/* Phase 3.5 refinement: same as the Players panel — single
          full-width column on every viewport. Horizontally stretched
          cards read better on desktop than a 2-up grid. */}
      <ul className="grid grid-cols-1 gap-3">
        {rows.map((row) => (
          <li key={row.id}>
            {direction === 'recharge' ? (
              <SubAgentRechargeCard
                row={row as AgentRechargeRow}
                busy={busyId === row.id}
                onApprove={openApproveRecharge}
                onApproveFull={approveAgentRechargeFull}
                onReject={rejectRecharge}
              />
            ) : (
              <SubAgentWithdrawalCard
                row={row as AgentWithdrawalRow}
                busy={busyId === row.id}
                onMarkPaid={openMarkPaid}
                onReject={rejectWithdrawal}
                onChanged={load}
              />
            )}
          </li>
        ))}
      </ul>

      {total > LIMIT && (
        <div className="flex items-center justify-end gap-2 pt-2 text-[12px] text-arena-600 dark:text-neon-200/80">
          <button disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
            className="grid place-items-center w-8 h-8 rounded-full bg-white/70 dark:bg-arena-700/40 border border-arena-200/60 dark:border-arena-600/50 disabled:opacity-40 hover:bg-white dark:hover:bg-arena-600/55">
            <Icon name="chevron_left" size={16} />
          </button>
          <span className="tabular-nums">{t('agent.peopleQueue.pagination', { from: String(offset + 1), to: String(Math.min(offset + LIMIT, total)), total: String(total) })}</span>
          <button disabled={offset + LIMIT >= total}
            onClick={() => setOffset(offset + LIMIT)}
            className="grid place-items-center w-8 h-8 rounded-full bg-white/70 dark:bg-arena-700/40 border border-arena-200/60 dark:border-arena-600/50 disabled:opacity-40 hover:bg-white dark:hover:bg-arena-600/55">
            <Icon name="chevron_right" size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Cards ─────────────────────────────────────────────────────────

function CardShell({
  children, accent, isDeposit,
}: { children: ReactNode; accent: 'in' | 'out'; isDeposit?: boolean }) {
  // Phase 10 (2026-05-12): when this card represents a DEPOSIT
  // request, we tint the entire card amber and double-strength the
  // border so the parent can spot deposit rows in a wallet-heavy
  // queue at a glance. Wallet rows keep the emerald/rose accents
  // (in/out flow), but DEPOSIT rows override with amber regardless
  // of direction because the deposit vs wallet distinction matters
  // more to the parent than recharge vs withdrawal here.
  const ring = isDeposit
    ? 'ring-2 ring-amber-300 dark:ring-amber-400/40'
    : accent === 'in'
      ? 'ring-1 ring-emerald-200/70 dark:ring-emerald-400/20'
      : 'ring-1 ring-rose-200/70 dark:ring-rose-400/20';
  const bg = isDeposit
    ? 'bg-amber-50/40 dark:bg-amber-500/[0.06]'
    : 'bg-white dark:bg-arena-700/40';
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl p-4
                  ${bg}
                  border border-arena-200/60 dark:border-arena-600/40 ${ring}
                  shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none`}
    >
      {children}
    </motion.div>
  );
}

// Phase 10: compact chip pair surfacing target (wallet vs deposit)
// and payment_method (UPI vs cash). Each chip is unmistakable at a
// glance — different colour, different icon — so the parent can
// triage the queue without reading the row body. The deposit chip
// only renders when target='deposit' (wallet is the silent default).
function TargetMethodChips({
  target, method,
}: { target?: 'wallet' | 'deposit'; method?: 'upi' | 'cash' }) {
  const t = useT();
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {target === 'deposit' && (
        <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5
                         text-[9.5px] font-extrabold uppercase tracking-wider
                         bg-amber-100 text-amber-800
                         dark:bg-amber-500/20 dark:text-amber-200
                         ring-1 ring-amber-300/60 dark:ring-amber-400/40">
          <Icon name="account_balance" size={11} filled />
          {t('agent.agentRequests.card.chip.deposit')}
        </span>
      )}
      {method === 'cash' ? (
        <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5
                         text-[9.5px] font-extrabold uppercase tracking-wider
                         bg-orange-100 text-orange-800
                         dark:bg-orange-500/20 dark:text-orange-200
                         ring-1 ring-orange-300/60 dark:ring-orange-400/40">
          <Icon name="payments" size={11} filled />
          {t('agent.agentRequests.card.chip.cash')}
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5
                         text-[9.5px] font-extrabold uppercase tracking-wider
                         bg-sky-100 text-sky-800
                         dark:bg-sky-500/20 dark:text-sky-200
                         ring-1 ring-sky-300/60 dark:ring-sky-400/40">
          <Icon name="qr_code_2" size={11} filled />
          {t('agent.agentRequests.card.chip.upi')}
        </span>
      )}
    </div>
  );
}

function FlowChip({ dir, amount }: { dir: 'in' | 'out'; amount: number }) {
  return (
    <span className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                      text-[14px] font-bold tabular-nums
                      ${dir === 'in'
                        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300'
                        : 'bg-rose-50 text-rose-700 dark:bg-rose-400/15 dark:text-rose-300'}`}>
      <Icon name={dir === 'in' ? 'arrow_upward' : 'arrow_downward'} size={14} />
      {inr(amount)}
    </span>
  );
}

function StateBadge({ state, kind }: { state: string; kind: 'recharge' | 'withdrawal' }) {
  const t = useT();
  const label =
    state === 'pending'  ? t('agent.agentRequests.state.pending')
    : state === 'rejected' ? t('agent.agentRequests.state.rejected')
    : state === 'expired' ? t('agent.agentRequests.state.expired')
    : kind === 'withdrawal' && state === 'paid'     ? t('agent.agentRequests.state.manuallyPaid')
    : kind === 'recharge'   && state === 'approved' ? t('agent.agentRequests.state.accepted')
    : state;
  const tone =
    state === 'pending'  ? 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-400/15 dark:text-amber-200 dark:border-amber-400/30'
    : state === 'rejected' ? 'bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-400/15 dark:text-rose-200 dark:border-rose-400/30'
    : state === 'expired' ? 'bg-arena-100/70 text-arena-700 border-arena-200/60 dark:bg-arena-700/40 dark:text-neon-200/65 dark:border-arena-600/50'
    : 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-400/15 dark:text-emerald-200 dark:border-emerald-400/30';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-bold border ${tone}`}>
      {label}
    </span>
  );
}

function ActionBtn({ tone, icon, label, onClick, busy }: {
  tone: 'success' | 'danger' | 'neutral'; icon: string; label: string; onClick: () => void; busy?: boolean;
}) {
  const M = {
    success: 'bg-emerald-600 text-white hover:bg-emerald-500 active:bg-emerald-700',
    danger:  'bg-white text-rose-700 border border-rose-200 hover:bg-rose-50 dark:bg-arena-700/40 dark:text-rose-300 dark:border-rose-400/30 dark:hover:bg-rose-400/10',
    neutral: 'bg-white text-arena-700 border border-arena-200/60 hover:bg-arena-50/40 dark:bg-arena-700/40 dark:text-neon-200/80 dark:border-arena-600/50 dark:hover:bg-arena-600/55',
  } as const;
  return (
    <button onClick={onClick} disabled={busy}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold transition disabled:opacity-50 active:scale-95 ${M[tone]}`}>
      <Icon name={busy ? 'progress_activity' : icon} size={14} className={busy ? 'animate-spin' : ''} />
      {label}
    </button>
  );
}

function SubAgentRechargeCard({ row, busy, onApprove, onApproveFull, onReject }: {
  row: AgentRechargeRow; busy: boolean;
  onApprove: (id: number) => void; onApproveFull: (row: AgentRechargeRow) => void; onReject: (id: number) => void;
}) {
  const t = useT();
  const isDeposit = row.target === 'deposit';
  const isCash    = row.payment_method === 'cash';
  return (
    <CardShell accent="in" isDeposit={isDeposit}>
      <div className="flex items-start gap-3 mb-3">
        <AdminAvatar url={row.agent_avatar_url} username={row.agent_username} size="sm" />
        <div className="min-w-0 flex-1">
          <Link to={`/people/agents/${row.agent_id}`}
                className="text-[13.5px] font-semibold text-arena-900 dark:text-white truncate hover:underline flex items-center gap-1.5 flex-wrap">
            {(((row.agent_display_name) || "").trim()) || "—"}
            <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-400/15 px-1.5 py-0.5 rounded">{t('agent.agentRequests.card.agentPill')}</span>
            <TargetMethodChips target={row.target} method={row.payment_method} />
          </Link>
          {/* Phase C4 — Identity Stack. Direct sub-agent → full
              visibility: @username + phone. The agent_email line is
              dropped (phone is the primary contact channel for queue
              triage; admins keep email visibility on their own queue). */}
          <div className="text-[11px] text-arena-600 dark:text-neon-200/70 truncate font-mono">
            @{row.agent_username} <span className="text-arena-400 dark:text-neon-200/40">·</span> #{row.agent_id}
          </div>
          {row.agent_phone && (
            <div className="text-[11px] text-arena-700 dark:text-neon-200/80 truncate tabular-nums">
              <PhoneContact phone={row.agent_phone} name={row.agent_display_name ?? row.agent_username} iconSize={11} className="tabular-nums" />
            </div>
          )}
          <div className="text-[10.5px] text-arena-500 dark:text-neon-200/65 mt-0.5">{fmtDate(row.created_at)}</div>
        </div>
        {/* Phase 18 UI fix — proof affordance in top-right column. */}
        <div className="flex flex-col items-end gap-2 shrink-0">
          <FlowChip dir="in" amount={row.amount} />
          <ProofImageViewer
            state={proofStateFor(row.proof_uploaded_at, row.proof_image_url)}
            endpoint={`/admin/me-agent/payment-requests/agent-recharge/${row.id}/proof`}
            apiGet={(p) => adminApi.get(p)} />
        </div>
      </div>

      <div className="rounded-xl bg-arena-50/60 dark:bg-arena-700/30 border border-arena-200/60 dark:border-arena-600/50 px-3 py-2 mb-3 space-y-1">
        <div className="flex items-center gap-2 text-[11px]">
          <span className="text-arena-600 dark:text-neon-200/80">
            {isCash ? t('agent.agentRequests.card.cashMarkerLabel') : t('agent.agentRequests.card.upiTxnLabel')}
          </span>
          <span className="font-mono text-arena-900 dark:text-white truncate">{row.transaction_id}</span>
        </div>
        {row.agent_note && (
          <div className="text-[11px] text-arena-700 dark:text-neon-200/75 italic">"{row.agent_note}"</div>
        )}
        {row.approved_amount != null && row.approved_amount !== row.amount && (
          <div className="text-[11px] text-amber-700 dark:text-amber-300">
            {t('agent.agentRequests.card.partialApproved', { approved: inr(row.approved_amount), requested: inr(row.amount) })}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          <StateBadge state={row.state} kind="recharge" />
        </div>
        {row.state === 'pending' ? (
          <div className="flex items-center gap-2">
            <ActionBtn tone="danger"  icon="close" label={t('agent.agentRequests.action.reject')} onClick={() => onReject(row.id)} busy={busy} />
            <ActionBtn tone="neutral" icon="tune"  label={t('agent.agentRequests.action.edit')} onClick={() => onApprove(row.id)} busy={busy} />
            <ActionBtn tone="success" icon="check" label={`${t('agent.agentRequests.action.approve')} ${inr(row.amount)}`} onClick={() => onApproveFull(row)} busy={busy} />
          </div>
        ) : row.decision_note ? (
          <div className="text-[10.5px] text-arena-600 dark:text-neon-200/80 italic truncate max-w-[60%]">"{row.decision_note}"</div>
        ) : null}
      </div>
    </CardShell>
  );
}

function SubAgentWithdrawalCard({ row, busy, onMarkPaid, onReject, onChanged }: {
  row: AgentWithdrawalRow; busy: boolean;
  onMarkPaid: (row: AgentWithdrawalRow) => void; onReject: (id: number) => void;
  onChanged?: () => void;
}) {
  const t = useT();
  const isDeposit = row.target === 'deposit';
  const isCash    = row.payment_method === 'cash';
  return (
    <CardShell accent="out" isDeposit={isDeposit}>
      <div className="flex items-start gap-3 mb-3">
        <AdminAvatar url={row.agent_avatar_url} username={row.agent_username} size="sm" />
        <div className="min-w-0 flex-1">
          <Link to={`/people/agents/${row.agent_id}`}
                className="text-[13.5px] font-semibold text-arena-900 dark:text-white truncate hover:underline flex items-center gap-1.5 flex-wrap">
            {(((row.agent_display_name) || "").trim()) || "—"}
            <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-400/15 px-1.5 py-0.5 rounded">{t('agent.agentRequests.card.agentPill')}</span>
            <TargetMethodChips target={row.target} method={row.payment_method} />
          </Link>
          {/* Phase C4 — Identity Stack (Name / @username / phone). */}
          <div className="text-[11px] text-arena-600 dark:text-neon-200/70 truncate font-mono">
            @{row.agent_username} <span className="text-arena-400 dark:text-neon-200/40">·</span> #{row.agent_id}
          </div>
          {row.agent_phone && (
            <div className="text-[11px] text-arena-700 dark:text-neon-200/80 truncate tabular-nums">
              <PhoneContact phone={row.agent_phone} name={row.agent_display_name ?? row.agent_username} iconSize={11} className="tabular-nums" />
            </div>
          )}
          <div className="text-[10.5px] text-arena-500 dark:text-neon-200/65 mt-0.5">{fmtDate(row.created_at)}</div>
        </div>
        {/* Phase 18 UI fix — payout-proof cell in the top-right
            column, far from the bottom-right Mark Paid action. */}
        <div className="flex flex-col items-end gap-2 shrink-0">
          <FlowChip dir="out" amount={row.amount} />
          <PayoutProofCell
            row={row}
            uploadEndpoint={`/admin/me-agent/payment-requests/agent-withdrawal/${row.id}/payout-proof`}
            viewEndpoint={`/admin/me-agent/payment-requests/agent-withdrawal/${row.id}/payout-proof`}
            onUploaded={() => onChanged?.()}
          />
        </div>
      </div>

      <div className="rounded-xl bg-arena-50/60 dark:bg-arena-700/30 border border-arena-200/60 dark:border-arena-600/50 px-3 py-2 mb-3 space-y-1">
        <div className="flex items-center gap-2 text-[11px]">
          <span className="text-arena-600 dark:text-neon-200/80">
            {isCash ? t('agent.agentRequests.card.handoffMarkerLabel') : t('agent.agentRequests.card.payToUpiLabel')}
          </span>
          <span className="font-mono text-arena-900 dark:text-white truncate">{row.upi_id}</span>
        </div>
        {row.whatsapp_number && (
          <a href={`https://wa.me/${row.whatsapp_number.replace(/\D/g, '')}`} target="_blank" rel="noreferrer"
             className="text-[11px] inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300 hover:underline">
            <WhatsAppIcon size={11} /> {t('agent.agentRequests.card.whatsapp', { number: row.whatsapp_number })}
          </a>
        )}
        {row.agent_note && (
          <div className="text-[11px] text-arena-700 dark:text-neon-200/75 italic">"{row.agent_note}"</div>
        )}
        {row.approved_amount != null && row.approved_amount !== row.amount && (
          <div className="text-[11px] text-amber-700 dark:text-amber-300">
            {t('agent.agentRequests.card.partialPaid', { approved: inr(row.approved_amount), requested: inr(row.amount), refund: inr(row.amount - row.approved_amount) })}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          <StateBadge state={row.state} kind="withdrawal" />
          {row.paid_transaction_id && row.state === 'paid' && (
            <span className="text-[10.5px] text-emerald-700 dark:text-emerald-300 font-mono truncate">{t('agent.agentRequests.card.paidTxnPrefix', { txn: row.paid_transaction_id })}</span>
          )}
        </div>

        {(row.state === 'pending' || row.state === 'approved') ? (
          <div className="flex items-center gap-2">
            <ActionBtn tone="danger"  icon="close"    label={t('agent.agentRequests.action.reject')}   onClick={() => onReject(row.id)} busy={busy} />
            <ActionBtn tone="success" icon="payments" label={t('agent.agentRequests.action.markPaid')} onClick={() => onMarkPaid(row)}  busy={busy} />
          </div>
        ) : row.decision_note ? (
          <div className="text-[10.5px] text-arena-600 dark:text-neon-200/80 italic truncate max-w-[60%]">"{row.decision_note}"</div>
        ) : null}
      </div>
    </CardShell>
  );
}

function Banner({ tone, children }: { tone: 'error' | 'success'; children: ReactNode }) {
  const M = {
    error:   'bg-rose-50 border-rose-200 text-rose-800 dark:bg-rose-500/10 dark:border-rose-400/30 dark:text-rose-200',
    success: 'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-500/10 dark:border-emerald-400/30 dark:text-emerald-200',
  } as const;
  return (
    <div className={`rounded-2xl border px-4 py-3 text-[13px] ${M[tone]}`}>{children}</div>
  );
}
